import { autoinject } from "aurelia-framework";
import { HttpClient } from "aurelia-fetch-client";
import { Location } from "../models/location";
import { LocationHierarchy } from "../models/location-hierarchy";
import { MeasurementViewModel } from "../models/measurement-view-model";
import { DigitalOutputToggleRequestViewModel } from "../models/digital-output-toggle-request-view-model";
import { MeasurementRequestViewModel } from "../models/measurement-request-view-model";
import { DeviceMeasurementRequestViewModel } from "../models/device-measurement-request-view-model";
import { TreeNode } from "../pages/notification-rules/tree-node";
import { Property } from "../models/property";
import { getLocationHierarchy, addLocation } from "../store/actions/location-actions";
import { WebApiResponse } from "../models/web-api-response";
import { Structure } from "../models/structure";
import { NotificationRecipient } from "../models/notification-recipient";
import { AddDeviceRequestViewModel } from "../models/add-device-request-view-model";
import { Device } from "../models/device";
import { IApiError } from "../models/application-error";
import { applicationLogger } from "../logging/logger";
import { GpsMap } from "../models/gps-map";

@autoinject
export class Api {
	httpClient: HttpClient;

	constructor(httpClient: HttpClient) {
		this.httpClient = httpClient;
	}

	getGpsMapping(locationId: string): Promise<WebApiResponse<Array<GpsMap>>>{
		return this.httpClient.fetch(`api/GpsMapping/${locationId}`,
				{
					method: "GET",
					//body: JSON.stringify(locationId),
					headers: {
						"Content-Type": "application/json",
						"Accept": "application/json"
					}
				})
			.then(result => result.json() as Promise<WebApiResponse<Array<GpsMap>>>)
			.then(response => { return response; })
			.catch(error => {
				const apiError = error as IApiError;
				applicationLogger.error(apiError.message);
				return null;
			});
	}

	getGraphData(graphRequest: DeviceMeasurementRequestViewModel): Promise<WebApiResponse<Array<MeasurementViewModel>>> {
		return this.httpClient.fetch("api/GraphMeasurements",
			{
				method: "POST",
				body: JSON.stringify(graphRequest),
				headers: {
					"Content-Type": "application/json",
					"Accept": "application/json"
				}
			})
			.then(result => result.json() as Promise<WebApiResponse<Array<MeasurementViewModel>>>)
			.then(response => { return response; })
			.catch(error => {
				const apiError = error as IApiError;
				applicationLogger.error(apiError.message);
				return null;
			});
	}

	// api/RulesTree
	getRulesTree(locationId: string): Promise<TreeNode> {
		return this.httpClient.fetch(`api/RulesTree/${locationId}`)
			.then(result => result.json() as Promise<TreeNode>)
			.then(treeNode => { return treeNode; });
	}

	getNotificationRecipients(locationId: string): Promise<Array<NotificationRecipient>> {
		return this.httpClient.fetch(`api/Notifications/Recipients/?locationId=${locationId}`)
			.then(result => result.json() as Promise<Array<NotificationRecipient>>)
			.then(array => { return array; });
	}

	toggleCurtain(digitalOutputToggleRequest: DigitalOutputToggleRequestViewModel): Promise<WebApiResponse<boolean>> {
		return this.httpClient.fetch(`api/CurtainController`,
				{
					method: "POST",
					body: JSON.stringify(digitalOutputToggleRequest),
					headers: {
						"Content-Type": "application/json",
						"Accept": "application/json"
					}
				})
			.then(result => result.json() as Promise<WebApiResponse<boolean>>)
			.then(result => { return result; })
			.catch(error => {
				return error;
			});
	}

	toggleZoneCurtains(digitalOutputToggleRequest: DigitalOutputToggleRequestViewModel): Promise<WebApiResponse<boolean>> {
		return this.httpClient.fetch(`api/ZoneCurtainController`,
				{
					method: "POST",
					body: JSON.stringify(digitalOutputToggleRequest),
					headers: {
						"Content-Type": "application/json",
						"Accept": "application/json"
					}
				})
			.then(result => result.json() as Promise<WebApiResponse<boolean>>)
			.then(result => { return result; })
			.catch(error => {
				return error;
			});
	}

	toggleLocationCurtains(digitalOutputToggleRequest: DigitalOutputToggleRequestViewModel): Promise<WebApiResponse<boolean>> {
		return this.httpClient.fetch(`api/LocationCurtainController`,
				{
					method: "POST",
					body: JSON.stringify(digitalOutputToggleRequest),
					headers: {
						"Content-Type": "application/json",
						"Accept": "application/json"
					}
				})
			.then(result => result.json() as Promise<WebApiResponse<boolean>>)
			.then(result => { return result; })
			.catch(error => {
				return error;
			});
	}

	toggleDigitalOutput(digitalOutputToggleRequest: DigitalOutputToggleRequestViewModel): Promise<WebApiResponse<boolean>> {
		return this.httpClient.fetch(`api/DirectMessage`,
			{
				method: "POST",
				body: JSON.stringify(digitalOutputToggleRequest),
				headers: {
					"Content-Type": "application/json",
					"Accept": "application/json"
				}
			})
		.then(result => result.json() as Promise<WebApiResponse<boolean>>)
		.then(result => { return result; })
		.catch(error => {
			return error;
		});
	}

	toggleValve(digitalOutputToggleRequest: DigitalOutputToggleRequestViewModel): Promise<WebApiResponse<boolean>> {
		return this.httpClient.fetch(`api/CloudToDeviceMessage`,
				{
					method: "POST",
					body: JSON.stringify(digitalOutputToggleRequest),
					headers: {
						"Content-Type": "application/json",
						"Accept": "application/json"
					}
				})
			.then(result => result.json() as Promise<WebApiResponse<boolean>>)
			.then(result => { return result; })
			.catch(error => {
				return error;
			});

	}

	addOrUpdateNotificationRecipient(recipient: NotificationRecipient): Promise<WebApiResponse<NotificationRecipient>> {
		return this.httpClient.fetch(`api/Notifications/Recipient`,
			{
				method: "POST",
				body: JSON.stringify(recipient),
				headers: {
					"Content-Type": "application/json",
					"Accept": "application/json"
				}
			})
			.then(result => result.json() as Promise<WebApiResponse<Property>>)
			.then(result => { return result; })
			.catch(error => {
				return error;
			});
	}

	addOrUpdateProperty(type: string, id: string, property: Property): Promise<WebApiResponse<Property>> {
		return this.httpClient.fetch(`api/${type}/${id}/property`,
			{
				method: "POST",
				body: JSON.stringify(property),
				headers: {
					"Content-Type": "application/json",
					"Accept": "application/json"
				}
			})
			.then(result => result.json() as Promise<WebApiResponse<Property>>)
			.then(result => { return result; })
			.catch(error => {
				return error;
			});
	}

	deleteProperty(type: string, id: string, property: Property): Promise<WebApiResponse<Property>> {
		return this.httpClient.fetch(`api/${type}/${id}/property`,
			{
				method: "DELETE",
				body: JSON.stringify(property),
				headers: {
					"Content-Type": "application/json",
					"Accept": "application/json"
				}
			})
			.then(result => result.json() as Promise<WebApiResponse<Property>>)
			.then(result => { return result; })
			.catch(error => {
				return error;
			});
	}

	addIotHubDevice(addDeviceRequestViewModel: AddDeviceRequestViewModel): Promise<Device> {

		return this.httpClient.fetch("api/device",
			{
				method: "POST",
				body: JSON.stringify(addDeviceRequestViewModel),
				headers: {
					"Content-Type": "application/json",
					"Accept": "application/json"
				}
			})
			.then(result => result.json() as Promise<Device>)
			.then(device => { return device; });
	}

	getMeasurements(measurementRequest: MeasurementRequestViewModel): Promise<Array<MeasurementViewModel>> {

		return this.httpClient.fetch("api/CurrentMeasurements",
			{
				method: "POST",
				body: JSON.stringify(measurementRequest),
				headers: {
					"Content-Type": "application/json",
					"Accept": "application/json"
				}
			})
			.then(result => result.json() as Promise<Array<MeasurementViewModel>>)
			.then(measurements => { return measurements; });
	}

	getWarnings(measurementRequest: MeasurementRequestViewModel): Promise<Array<MeasurementViewModel>> {

		return this.httpClient.fetch("api/CurrentWarnings",
			{
				method: "POST",
				body: JSON.stringify(measurementRequest),
				headers: {
					"Content-Type": "application/json",
					"Accept": "application/json"
				}
			})
			.then(result => result.json() as Promise<Array<MeasurementViewModel>>)
			.then(warnings => { return warnings; });
	}

	getLocationHierarchy(version: number): Promise<LocationHierarchy> {

		return this.httpClient.fetch(`api/LocationHierarchy/${version}`)
			.then(result => result.json() as Promise<LocationHierarchy>)
			.then(registry => { return registry; });
	}

	addLocation(location: Location): Promise<Location> {

		return this.httpClient.fetch("api/location",
			{
				method: "post",
				body: JSON.stringify(location),
				headers: {
					"Content-Type": "application/json",
					"Accept": "application/json"
				}
			})
			.then(result => result.json() as Promise<Location>)
			.then(location => { return location; });
	}

	getLocation(locationId: string): Promise<Location> {
		return this.httpClient.fetch(`api/location/${locationId}`,
				{
					method: "get",
					headers: {
						"Content-Type": "application/json",
						"Accept": "application/json"
					}
				})
			.then(result => result.json() as Promise<Location>)
			.then(location => { return location; });
	}

	getLocations(): Promise<Array<Location>> {

		return this.httpClient.fetch("api/location",
			{
				method: "get",
				headers: {
					"Content-Type": "application/json",
					"Accept": "application/json"
				}
			})
			.then(result => result.json() as Promise<Array<Location>>)
			.then(locations => { return locations; });
	}

	getStructure(structureId: string): Promise<WebApiResponse<Structure>> {
		return this.httpClient.fetch(`api/structure/${structureId}`,
				{
					method: "get",
					headers: {
						"Content-Type": "application/json",
						"Accept": "application/json"
					}
				})
			.then(result => result.json() as Promise<WebApiResponse<Structure>>)
			.then(response => { return response; });
	}

	updateStructure(structure: Structure): Promise<WebApiResponse<Structure>> {

		return this.httpClient.fetch("api/structure/" + structure.id,
				{
					method: "patch",
					body: JSON.stringify(structure),
					headers: {
						"Content-Type": "application/json",
						"Accept": "application/json"
					}
				})
			.then(result => result.json() as Promise<WebApiResponse<Structure>>)
			.then(response => { return response; })
			.catch(error => {
				return error;
			});
			
	}

	addDevice(addDeviceRequest: AddDeviceRequestViewModel) {
		return this.httpClient.fetch("api/location",
			{
				method: "post",
				body: JSON.stringify(addDeviceRequest),
				headers: {
					"Content-Type": "application/json",
					"Accept": "application/json"
				}
			})
			.then(result => result.json() as Promise<Device>)
			.then(device => { return device; });
	}
}